import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { RequestNextApi } from "../../../../api/Request";
import { getCookie } from "../../../../utils/cookieHandle";
import { ShowNotification } from "../../../../utils/notification/snackbarUtils";

const paymentMethods = [
  {
    label: "Credit Card",
    value: "CC",
  },
  {
    label: "Check",
    value: "Check",
  },
  {
    label: "All",
    value: "All",
  },
];

const formats = [
  {
    label: "JSON",
    value: "json",
  },
  {
    label: "HTML",
    value: "html",
  },
  {
    label: "PDF",
    value: "pdf",
  },
];

const quickDateOptions = [
  {
    label: "2 Months Prior",
    value: "2_months_prior",
  },
  {
    label: "1 Month Prior", 
    value: "1_month_prior",
  },
  {
    label: "Current Month",
    value: "current_month",
  },
];

const downloadFile = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
const openInNewTab = (blob) => {
  const url = URL.createObjectURL(blob);
  const newTab = window.open(url, "_blank");
  if (newTab) {
    newTab.onload = () => URL.revokeObjectURL(url);
  } else {
    console.error("Failed to open new tab");
  }
};

function DepositReport() {
  const [startDate, setStartDate] = React.useState(
    dayjs().subtract(1, "month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = React.useState(dayjs().format("YYYY-MM-DD"));
  const [showStripe, setShowStripe] = React.useState(false);
  const [showDetail, setShowDetail] = React.useState(false);
  const [paymentMethod, setPaymentMethod] = React.useState("All");
  const [format, setFormat] = React.useState("pdf");
  const [loading, setLoading] = React.useState(false);
  const [quickDateSelect, setQuickDateSelect] = React.useState("");

  const handleQuickDateSelect = (event) => {
    const value = event.target.value;
    setQuickDateSelect(value);
    
    const today = dayjs();
    let start, end;

    switch (value) {
      case "2_months_prior":
        start = today.subtract(2, "month").startOf("month").format("YYYY-MM-DD");
        end = today.subtract(2, "month").endOf("month").format("YYYY-MM-DD");
        break;
      case "1_month_prior":
        start = today.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
        end = today.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
        break;
      case "current_month":
        start = today.startOf("month").format("YYYY-MM-DD");
        end = today.format("YYYY-MM-DD");
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
  };

  const onRunReport = async () => {
    try {
      setLoading(true);
      const result = await RequestNextApi.post(
        "/admin/report/run-deposit-report",
        {
          jwtToken: getCookie("jwt-token"),
          startDate,
          endDate,
          showStripe,
          showDetail,
          paymentMethod,
          export_type: format,
        },
        {
          ...(format == "pdf" && { responseType: "blob" }),
        }
      );
      if (
        (format === "html" &&
          result.data &&
          typeof result.data === "string") ||
        (format === "json" && result.data.success) || (format === "pdf" && result.data)
      ) {
        if (format === "json") {
          const jsonString = JSON.stringify(
            {
              transactions: result.data.transactions,
              journal: result.data.journal,
            },
            null,
            2
          );
          const blob = new Blob([jsonString], { type: "application/json" });
          downloadFile(
            blob,
            `deposit_report_${startDate}_to_${endDate}` + ".json"
          );
        }
        if (format === "html") {
          const blob = new Blob([result.data], { type: "text/html" });
          openInNewTab(blob);
        }
        if (format === "pdf") {
          const blob = new Blob([result.data], { type: "application/pdf" });
          openInNewTab(blob);
        }
      } else {
        ShowNotification.error(result.data.message);
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-[1px] border-solid border-primary-100 rounded-xl p-[20px]">
      <Typography variant="h4" className="font-normal">
        Deposit Report
      </Typography>
      <div className="flex gap-2 mt-4 items-center">
        <FormControl size="small" fullWidth>
          <InputLabel id="quick-date-select-label">Quick Date Select</InputLabel>
          <Select
            labelId="quick-date-select-label"
            id="quick-date-select"
            value={quickDateSelect}
            label="Quick Date Select"
            onChange={handleQuickDateSelect}
            color="secondary"
          >
            {quickDateOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Start date"
          color="secondary"
          focused
          size="small"
          type="date"
          fullWidth
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setQuickDateSelect("");
          }}
        />
        <TextField
          label="End date"
          color="secondary"
          focused
          size="small"
          type="date"
          fullWidth
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
          }}
        />
      </div>
      <div className="flex items-center mt-4">
        <FormControlLabel
          control={<Checkbox color="secondary" />}
          label="Show stripe"
          checked={showStripe}
          onChange={(e) => {
            setShowStripe(e.target.checked);
          }}
        />
        <FormControlLabel
          control={<Checkbox color="secondary" />}
          label="Show details"
          checked={showDetail}
          onChange={(e) => {
            setShowDetail(e.target.checked);
          }}
        />
      </div>

      <div className="mt-6 flex gap-2">
        <div className="flex gap-2 flex-1">
          <FormControl sx={{}} fullWidth>
            <InputLabel id="payment-method-deposit-report" color="third">
              Payment method
            </InputLabel>
            <Select
              value={paymentMethod}
              placeholder="Payment method"
              size="small"
              label="Payment method"
              onChange={(e) => {
                setPaymentMethod(e.target.value);
              }}
              color="secondary"
            >
              {paymentMethods.map((item, index) => {
                return (
                  <MenuItem value={item.value} key={index}>
                    {item.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl sx={{}} fullWidth>
            <InputLabel id="format-deposit-report" color="third">
              Format
            </InputLabel>
            <Select
              value={format}
              placeholder="Format"
              size="small"
              label="Format"
              onChange={(e) => {
                setFormat(e.target.value);
              }}
              color="secondary"
            >
              {formats.map((item, index) => {
                return (
                  <MenuItem value={item.value} key={index}>
                    {item.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
        <Button
          variant="contained"
          className="bg-secondary-500 hover:bg-secondary-500 w-[100px]"
          onClick={() => {
            onRunReport();
          }}
          type="button"
          size="small"
          disabled={loading}
        >
          {
            loading ?
              <CircularProgress color="secondary" size={20} />
              :
              "Run"
          }
        </Button>
      </div>
    </div>
  );
}

export default DepositReport;
